<!--
@component
Flag

See markup below.
-->

<script lang="ts" module>
  /**
   * Module-level cache of flag image URLs that have already been decoded
   * successfully in the browser. Because this lives at the module scope it
   * is shared across every `<Flag>` instance and survives component
   * mount/unmount cycles (e.g. tab switches, route changes within the SPA).
   * Once a flag has loaded, subsequent renders use it immediately without
   * flashing the fallback or re-running `image.decode()`.
   */
  const decodedFlags = new Set<string>();
</script>

<script lang="ts">
  import { untrack } from 'svelte';
  import getPath from '$utils/getPath';
  import getTeamPath from '$utils/getTeamPath';
  import { appState } from '$lib/stores/index.svelte';
  import { FALLBACK_FLAGS } from './fallbackImages';

  type Props = {
    circle?: boolean;
    country?: string;
    link?: boolean;
    width?: string;
    loading?: 'lazy' | 'eager';
  };

  let {
    circle = false,
    country = '',
    link = false,
    width = '1rem',
    loading = 'lazy',
  }: Props = $props();

  const FALLBACK_SLUG = 'UNKNOWN';

  const INTRINSIC = {
    circles: { width: 20, height: 20 },
    rects: { width: 30, height: 20 },
  } as const;

  const team = $derived(appState.teams?.getTeam(country));
  const shape = $derived<'circles' | 'rects'>(circle ? 'circles' : 'rects');
  const slug = $derived(team?.slug || FALLBACK_SLUG);
  const intrinsic = $derived(INTRINSIC[shape]);

  const flagSrc = $derived(getPath(`images/flags/${shape}/${slug}.png`));
  const fallbackSrc = $derived(FALLBACK_FLAGS[shape]);
  const altText = $derived(`Flag of ${team?.name || 'country'}`);
  // Defer to `getTeamPath` so the embed branch (`appState.embed`)
  // flows through automatically. Fall back to `#` when the team
  // hasn't resolved yet so the link is still inert rather than
  // pointing at an unrelated page.
  const href = $derived(
    team?.slug ?
      (getTeamPath({ slug: team.slug, name: team.name })?.url ?? '#')
    : '#'
  );

  /**
   * Seed reactive state from the module-level cache so a remount of this
   * component for an already-loaded flag renders the real image on the
   * very first paint (no fallback flash, no redundant decode).
   */
  let loadedFlagSrc = $state<string | null>(
    untrack(() => (decodedFlags.has(flagSrc) ? flagSrc : null))
  );
  const currentSrc = $derived(
    loadedFlagSrc === flagSrc ? flagSrc : fallbackSrc
  );

  $effect(() => {
    if (slug === FALLBACK_SLUG) {
      loadedFlagSrc = null;
      return;
    }

    const target = flagSrc;

    // Fast path: already decoded in this session.
    if (decodedFlags.has(target)) {
      loadedFlagSrc = target;
      return;
    }

    let cancelled = false;

    const img = new Image();
    img.src = target;
    img
      .decode()
      .then(() => {
        decodedFlags.add(target);
        if (!cancelled) loadedFlagSrc = target;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  });
</script>

{#snippet flagImg()}
  <img
    src={currentSrc}
    alt={altText}
    width={intrinsic.width}
    height={intrinsic.height}
    {loading}
    decoding="async"
    draggable="false"
    style:width
    style:height="auto"
  />
{/snippet}

<figure style:width style:height="auto">
  {#if link}
    <a
      {href}
      target={appState.embed ? '_top' : undefined}
      class:loading={appState.isLoading}
    >
      {@render flagImg()}
    </a>
  {:else}
    {@render flagImg()}
  {/if}
</figure>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  figure {
    padding: 0;
    margin: 0;
  }

  img {
    margin: 0;
  }

  a {
    &.loading {
      pointer-events: none;
      cursor: default;
    }

    @include mixins.hover-supported() {
      opacity: 0.9;
      overflow: visible;
    }
  }
</style>
